/* eslint-disable react/prop-types */
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import moment from 'moment';
import React from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { ContextStore } from '../../store/contextStore';
import { asJSONPath, EMDASH } from '../../utils';
import { useIsWidthUp } from '../../utils';
import TripFlowDay from './TripEditFlowDay';

const useStyles = makeStyles((theme) => ({
    root: {
        border: 'solid 1px #D8D8D8',
        margin: '0 1rem',
        borderRadius: '0 0 12px 12px',
        backgroundColor: '#fff',
        [theme.breakpoints.down('md')]: {
            border: 0,
            margin: 0,
            [theme.breakpoints.down('sm')]: {
                paddingBottom: '8rem !important',
            },
        },
        minHeight: `calc(${theme.cp.contentHeight} - 9rem)`,
        marginBottom: '2rem',
    },
    addDayButton: {
        width: '141px !important',
        borderColor: '#D8D8D8 !important',
        borderRadius: '8px !important',
        height: '40px !important',
    },
}));

const tabDayButtonSx = (isSmallScreen) => ({
    display: 'flex',
    width: isSmallScreen ? 'auto' : '110px',
    minWidth: isSmallScreen ? 96 : undefined,
    height: '36px',
    borderRadius: '20px',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    flexShrink: 0,
    padding: isSmallScreen ? '0 10px' : '0 16px',
    boxSizing: 'border-box',
});

const SortableDayTab = ({ id, dayIndex, selected, onSelect, trip, dayLabels, isSmallScreen }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        position: 'relative',
        zIndex: isDragging ? 2 : 'auto',
    };
    // CP-8349: a guide group is bespoke but its dates are synthetic, so it gets the
    // date-less "DAY n" labels like a non-bespoke tour.
    const label = !trip.is_bespoke || trip.is_staff_chat_group ? `DAY ${dayIndex + 1}` : dayLabels[dayIndex];

    return (
        <Box
            ref={setNodeRef}
            style={style}
            role="tab"
            tabIndex={selected ? 0 : -1}
            id={`full-width-tab-${dayIndex}`}
            aria-controls={`full-width-tabpanel-${dayIndex}`}
            aria-selected={selected}
            onClick={() => onSelect(dayIndex)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(dayIndex);
                }
            }}
            sx={{
                ...tabDayButtonSx(isSmallScreen),
                backgroundColor: selected ? '#007AFF' : '#D8D8D8',
                color: selected ? 'white' : '#202020',
            }}
        >
            <Typography fontWeight={500} fontSize={12} noWrap sx={{ flex: 1, minWidth: 0 }}>
                {label}
            </Typography>
            <Box
                component="span"
                {...attributes}
                {...listeners}
                onClick={(e) => e.stopPropagation()}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    cursor: 'grab',
                    touchAction: 'none',
                    flexShrink: 0,
                    ml: 0.5,
                }}
            >
                <Tooltip title="Drag to reorder">
                    <DragIndicatorIcon sx={{ fontSize: '16px' }} />
                </Tooltip>
            </Box>
        </Box>
    );
};

const getDayLabels = (trip_start_date, days) => {
    const start_date = trip_start_date.clone();
    return days.map((day) => {
        const day_span = +Math.abs(day.day_span) || 1;
        const end_date = start_date.clone().add(day_span - 1, 'days');
        let label = '';
        label = start_date.format('MMM D');
        if (day_span > 1) {
            label = `${start_date.format('MMM D')}${EMDASH}${end_date.format('D')}`;
        }
        start_date.add(day_span, 'days');
        return label;
    });
};

const emptyDay = {
    day_span: 1,
    description: '',
    footer: '',
    id: null,
    image: null,
    sequence: 1,
    subtitle: '',
    timeslots: [],
    title: '',
};

export const TripEditFlow = (props) => {
    const history = useHistory();
    const urlParams = useParams();
    const location = useLocation();
    const isSmallScreen = !useIsWidthUp('sm');
    const { hasPerm } = React.useContext(ContextStore);

    const classes = useStyles();
    const trip = props.data;

    const days = trip.itinerary?.days || [];

    const start_date = moment.utc(trip.start_date);
    const end_date = moment.utc(start_date).add(Math.ceil(trip.length / 86400) - 1, 'days');
    const n_days = days.length;
    const dayLabels = getDayLabels(start_date, days, trip.start_timezone);
    const subtab = +urlParams.subtab;
    const currentTab = subtab && subtab >= 0 && subtab <= (trip.itinerary?.days || []).length ? subtab : 0;
    const hasWritePerm = hasPerm('tour', 'write');

    const sortableIds = React.useMemo(() => Array.from({ length: n_days }, (_, i) => String(i)), [n_days]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 6 },
        })
    );

    React.useEffect(() => {
        if (urlParams.subtab === void 0) {
            history.replace(`${location.pathname}/0`);
        } else if (urlParams.subtab !== currentTab) {
            const newPath = location.pathname.split('/');
            newPath.pop();

            history.replace(`${newPath.join('/')}/${currentTab}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTab, urlParams.id, urlParams.tab]);

    const handleTabChange = (newTab) => {
        history.push(`/trip/${urlParams.id}/${urlParams.tab}/${newTab}`);
    };

    const handleTabSequenceChange = ({ oldIndex, newIndex }) => {
        const minIndex = oldIndex < newIndex ? oldIndex : newIndex;
        const maxIndex = oldIndex < newIndex ? newIndex : oldIndex;
        const shiftLeft = oldIndex < newIndex;

        const newDays = (trip.itinerary?.days || []).map((d, i, oldDays) => {
            if (i === newIndex) {
                return { ...oldDays[oldIndex], sequence: i };
            } else if (shiftLeft && i >= minIndex && i < maxIndex) {
                return { ...oldDays[i + 1], sequence: i };
            } else if (!shiftLeft && i > minIndex && i <= maxIndex) {
                return { ...oldDays[i - 1], sequence: i };
            } else {
                return { ...d, sequence: i };
            }
        });
        props.onChange &&
            props.onChange({
                ...trip,
                itinerary: {
                    ...(trip.itinerary || {}),
                    days: newDays,
                },
            });
        handleTabChange(newIndex);
    };

    const onDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = Number(active.id);
        const newIndex = Number(over.id);
        if (Number.isNaN(oldIndex) || Number.isNaN(newIndex)) return;
        handleTabSequenceChange({ oldIndex, newIndex });
    };

    const addNewDay = () => {
        const lastDayIdx = days.length - 1;
        const lastDay = days[lastDayIdx];
        props.onChange &&
            props.onChange(
                asJSONPath(`itinerary.days[${lastDayIdx + 1}]`, {
                    ...emptyDay,
                    sequence: lastDay && lastDay.sequence ? lastDay.sequence + 1 : 1,
                    timeslots: [],
                })
            );

        handleTabChange(lastDayIdx + 1);
    };

    const onDayChange = (day, dayIdx) => {
        props.onChange && props.onChange(asJSONPath(`itinerary.days[${dayIdx}]`, day), props.idx);
    };

    const onDayDelete = (day, dayIdx) => {
        props.onChange &&
            props.onChange({
                ...trip,
                itinerary: {
                    ...(trip.itinerary || {}),
                    days: days.filter((d, i) => i !== dayIdx),
                },
            });
    };

    const onValidateDay = (key, canSave, errors) => {
        props.onValidate && props.onValidate(`itinerary.${key}`, canSave, errors);
    };

    const tabListStrategy = isSmallScreen ? horizontalListSortingStrategy : verticalListSortingStrategy;

    const dayTabList = (
        <Box
            role="tablist"
            sx={{
                display: 'flex',
                flexDirection: isSmallScreen ? 'row' : 'column',
                flexWrap: 'nowrap',
                alignItems: 'center',
                gap: 1,
                width: isSmallScreen ? 'max-content' : 'auto',
                backgroundColor: '#fff',
                borderRadius: isSmallScreen ? 0 : '12px 0 0 12px',
                py: isSmallScreen ? 0 : 0,
                pt: isSmallScreen ? 0 : 2,
                px: isSmallScreen ? 0 : 1,
            }}
        >
            {sortableIds.map((id, n) => (
                <SortableDayTab
                    key={id}
                    id={id}
                    dayIndex={n}
                    selected={currentTab === n}
                    onSelect={handleTabChange}
                    trip={trip}
                    dayLabels={dayLabels}
                    isSmallScreen={isSmallScreen}
                />
            ))}
        </Box>
    );

    const dayPicker = (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={sortableIds} strategy={tabListStrategy}>
                {isSmallScreen ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            width: '100%',
                            px: 2,
                            py: 1,
                            borderBottom: '1px solid',
                            borderColor: 'rgba(0, 0, 0, 0.08)',
                            boxSizing: 'border-box',
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                WebkitOverflowScrolling: 'touch',
                                scrollbarWidth: 'thin',
                                '&::-webkit-scrollbar': { height: 4 },
                            }}
                        >
                            {dayTabList}
                        </Box>
                        <Tooltip title="Add day">
                            <span style={{ display: 'inline-flex', flexShrink: 0 }}>
                                <IconButton
                                    color="primary"
                                    onClick={addNewDay}
                                    disabled={!hasWritePerm}
                                    aria-label="Add day"
                                    disableRipple
                                    sx={{
                                        flexShrink: 0,
                                        width: 40,
                                        height: 40,
                                        color: 'primary.main',
                                        '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.06)' },
                                    }}
                                >
                                    <AddIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                ) : (
                    dayTabList
                )}
            </SortableContext>
        </DndContext>
    );

    const addDayControl = isSmallScreen ? null : (
        <Button
            onClick={addNewDay}
            disabled={!hasWritePerm}
            className={classes.addDayButton}
            variant="outlined"
            sx={{ ml: 1, mt: 2, mb: 1 }}
        >
            <AddIcon sx={{ fontSize: 16 }} />
            <Typography fontWeight={500} fontSize={14} pt={0.25}>
                &nbsp; ADD DAY
            </Typography>
        </Button>
    );

    const panelPadding = isSmallScreen ? 1 : 2;

    return (
        <div className={classes.root}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isSmallScreen ? 'column' : 'row',
                    backgroundColor: '#fff',
                    borderRadius: isSmallScreen ? 0 : '12px',
                    alignItems: 'flex-start',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: isSmallScreen ? 'stretch' : 'center',
                        flexShrink: 0,
                        width: isSmallScreen ? '100%' : 'auto',
                    }}
                >
                    {dayPicker}
                    {addDayControl}
                </Box>
                <Box
                    role="tabpanel"
                    id={`full-width-tabpanel-${currentTab}`}
                    aria-labelledby={`full-width-tab-${currentTab}`}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        alignSelf: 'stretch',
                        p: panelPadding,
                        pt: isSmallScreen ? panelPadding : 2,
                        backgroundColor: '#fff',
                        borderRadius: isSmallScreen ? 0 : '0 12px 12px 0',
                    }}
                >
                    {n_days > 0 && days[currentTab] ? (
                        <TripFlowDay
                            setNewDocsAdded={props.setNewDocsAdded}
                            newDocsAdded={props.newDocsAdded}
                            onTripUpdate={props.onTripUpdate}
                            trip={trip}
                            startDate={start_date}
                            endDate={end_date}
                            day={days[currentTab]}
                            idx={currentTab}
                            dayLabel={dayLabels[currentTab]}
                            onChange={onDayChange}
                            onDelete={onDayDelete}
                            onValidate={onValidateDay}
                            isSmallScreen={isSmallScreen}
                            setClientErrors={props.setClientErrors}
                            disabled={!hasWritePerm}
                        />
                    ) : null}
                </Box>
            </Box>
        </div>
    );
};
